import type { CouplingRule } from '@/engine/dynamics';

/**
 * 港机码头 动力学耦合规则
 *
 * 物理逻辑：
 *  1. 起升载荷 ↑ → 载荷百分比 ↑（LOAD-PCT = 实际载荷 / 额定 65t × 100%）
 *  2. 起升速度 → 起升高度变化（速度驱动位置，近似积分用线性耦合）
 *  3. 小车速度 → 小车位置变化
 *  4. 小车运行 → 跑偏量累积（位置偏离基线越远，导向轮偏磨越重）
 *  5. 大车速度 → 大车位置变化
 *  6. 风速 ↑ → 大车被动位移（大风且未制动时，风载推着整机沿轨道滑行）
 *  7. 起升载荷 ↑ → 起升电机电流 ↑（重载电流大）
 *  8. 载荷百分比超 100% → PLC 报警数 ↑（超载触发报警）
 *  9. 风速超 25m/s → PLC 报警数 ↑（大风触发报警）
 */
export const portcraneCouplings: CouplingRule[] = [
  // 1. 起升载荷 → 载荷百分比（LOAD-PCT ≈ HOIST-LOAD / 65 × 100，gain 取 100/65≈1.54）
  {
    from: { equipmentId: 'HOIST', param: 'HOIST-LOAD' },
    to: { equipmentId: 'SENSOR-LOAD', param: 'LOAD-PCT' },
    gain: 1.54,      // 载荷 +1t → 百分比 +1.54%（在 0~150% 区间近似）
    baseline: 40,
    tau: 1,
  },

  // 2. 起升速度 → 起升高度（速度驱动位置，线性近似积分关系）
  {
    from: { equipmentId: 'HOIST', param: 'HOIST-SPD' },
    to: { equipmentId: 'HOIST', param: 'HOIST-POS' },
    gain: 0.1,       // 起升速度 +1m/min（高于基线）→ 高度 +0.1m
    baseline: 45,
    tau: 3,
  },

  // 3. 小车速度 → 小车位置
  {
    from: { equipmentId: 'TROLLEY', param: 'TROLLEY-SPD' },
    to: { equipmentId: 'TROLLEY', param: 'TROLLEY-POS' },
    gain: 0.1,       // 小车速度 +1m/min（高于基线）→ 位置 +0.1m
    baseline: 40,
    tau: 3,
  },

  // 4. 小车位置 → 小车跑偏量（运行越偏离中位基线，导向轮偏磨累积越重）
  {
    from: { equipmentId: 'TROLLEY', param: 'TROLLEY-POS' },
    to: { equipmentId: 'TROLLEY', param: 'TROLLEY-DEV' },
    gain: 0.1,       // 位置偏离基线 +1m → 跑偏 +0.1mm
    baseline: 30,
    tau: 5,
  },

  // 5. 大车速度 → 大车位置
  {
    from: { equipmentId: 'GANTRY', param: 'GANTRY-SPD' },
    to: { equipmentId: 'GANTRY', param: 'GANTRY-POS' },
    gain: 0.2,       // 大车速度 +1m/min（高于基线）→ 位置 +0.2m
    baseline: 15,
    tau: 5,
  },

  // 6. 风速 → 大车被动位移（仅大风未制动时风载推着整机滑行，baseline 取报警阈值 25）
  {
    from: { equipmentId: 'SENSOR-WIND', param: 'WIND-SPD' },
    to: { equipmentId: 'GANTRY', param: 'GANTRY-POS' },
    gain: 1.0,       // 风速 +1m/s（超 25 报警阈值）→ 大车被动位移 +1m
    baseline: 25,
    tau: 8,
  },

  // 7. 起升载荷 → 起升电机电流（重载电流大）
  {
    from: { equipmentId: 'HOIST', param: 'HOIST-LOAD' },
    to: { equipmentId: 'POWER', param: 'CURR' },
    gain: 2,         // 载荷 +1t → 电流 +2A
    baseline: 40,
    tau: 2,
  },

  // 8. 载荷百分比 → PLC 报警数（超 100% 即超载报警，baseline 取 100）
  {
    from: { equipmentId: 'SENSOR-LOAD', param: 'LOAD-PCT' },
    to: { equipmentId: 'PLC', param: 'PLC-ALARM' },
    gain: 0.5,       // 载荷百分比 +1%（超 100）→ 报警 +0.5 个
    baseline: 100,
    tau: 2,
  },

  // 9. 风速 → PLC 报警数（超 25m/s 即大风报警，baseline 取 25）
  {
    from: { equipmentId: 'SENSOR-WIND', param: 'WIND-SPD' },
    to: { equipmentId: 'PLC', param: 'PLC-ALARM' },
    gain: 0.5,       // 风速 +1m/s（超 25）→ 报警 +0.5 个
    baseline: 25,
    tau: 2,
  },
];
